import { chartingQueryKeys } from '@/features/charting-next/api/chartingCache';
import { fetchChartingCollectionByEntry } from '@/features/charting-next/api/chartingCollectionRepository';
import { fetchChartingOhlcSeries } from '@/features/charting-next/api/chartingInsightsRepository';
import { buildChartingCollectionCacheSignature } from '@/features/charting-next/models/chartingFinderSync';
import { isChartingMarketSortSupported } from '@/features/charting-next/models/chartingMarketQuery';
import { buildChartingResultSortOptions, resolveChartingResultSortValue, sortChartingResults } from '@/features/charting-next/models/chartingResultSorting';
import { useRemoteResource } from '@/features/charting-next/services/useRemoteResource';
import { computed, unref, watch } from '@vue/composition-api';

const buildEntrySwitchLabels = (variants = []) => {
    const baseLabelCount = variants.reduce((accumulator, variant) => {
        const key = String(variant.entryVariantBaseLabel || variant.entryTypeLabel || 'Entrada');

        accumulator.set(key, (accumulator.get(key) || 0) + 1);
        return accumulator;
    }, new Map());

    return variants.map((variant, index) => {
        const baseLabel = String(variant.entryVariantBaseLabel || variant.entryTypeLabel || 'Entrada');
        const duplicateBaseLabel = (baseLabelCount.get(baseLabel) || 0) > 1;
        let entrySwitchLabel = baseLabel;

        if (duplicateBaseLabel && variant.entryPriceLabel) {
            entrySwitchLabel = `${baseLabel} · ${variant.entryPriceLabel}`;
        } else if (duplicateBaseLabel) {
            entrySwitchLabel = `${baseLabel} ${index + 1}`;
        }

        return {
            ...variant,
            entrySwitchLabel
        };
    });
};

export function useChartingCollections({ sessionStore, route, entry, contextStore, syncSelectedTickerInRoute, finderRevision, marketExplorer, enableOhlcSeries = true, loadCollectionEntry = fetchChartingCollectionByEntry }) {
    const collectionCacheSignature = computed(() => buildChartingCollectionCacheSignature(entry.value, finderRevision?.value));

    const legacyCollectionResource = useRemoteResource({
        key: () => chartingQueryKeys.collection(sessionStore.uid, collectionCacheSignature.value),
        loader: () =>
            loadCollectionEntry({
                ...entry.value,
                query: route?.query || {}
            }),
        ttl: 20000,
        enabled: () => sessionStore.isAuthenticated && !marketExplorer.isActive.value
    });

    const activeResource = () => (marketExplorer.isActive.value ? marketExplorer.resource : legacyCollectionResource);
    const collectionResource = {
        data: computed(() => activeResource().data.value),
        error: computed(() => activeResource().error.value),
        status: computed(() => activeResource().status.value),
        isLoading: computed(() => activeResource().isLoading.value),
        isRefreshing: computed(() => activeResource().isRefreshing.value),
        isReady: computed(() => activeResource().isReady.value),
        isEmpty: computed(() => activeResource().isEmpty.value),
        load: (options) => activeResource().load ? activeResource().load(options) : activeResource().refresh(),
        refresh: () => activeResource().refresh()
    };

    const rawResults = computed(() => collectionResource.data.value?.items || []);
    const groupedResults = computed(() => {
        const groups = new Map();

        for (const item of rawResults.value) {
            const ticker = String(item?.ticker || '')
                .trim()
                .toUpperCase();

            if (!ticker) {
                continue;
            }

            if (!groups.has(ticker)) {
                groups.set(ticker, []);
            }

            groups.get(ticker).push(item);
        }

        return Array.from(groups.entries()).map(([ticker, variants]) => {
            const decoratedVariants = buildEntrySwitchLabels(variants);
            const selectedEntryVariantKey = contextStore.selectedEntryVariantKeys[ticker];
            const activeVariant = decoratedVariants.find((variant) => variant.entryVariantKey === selectedEntryVariantKey) || decoratedVariants[0];

            return {
                ...activeVariant,
                id: `${ticker}-group`,
                entryVariants: decoratedVariants,
                variantCount: decoratedVariants.length,
                hasMultipleEntries: decoratedVariants.length > 1,
                activeEntryVariantKey: activeVariant.entryVariantKey
            };
        });
    });
    const resultSortOptions = computed(() => {
        const options = buildChartingResultSortOptions({ entry: entry.value });
        return marketExplorer.isActive.value ? options.filter((option) => isChartingMarketSortSupported(option.value)) : options;
    });
    const resultSortValue = computed(() => resolveChartingResultSortValue(contextStore.resultSort, resultSortOptions.value));
    const results = computed(() => sortChartingResults(groupedResults.value, resultSortValue.value));

    watch(
        [results, () => route?.query?.ticker, () => entry.value.activeTickerHint],
        ([items, queryTicker, hintedTicker]) => {
            const candidateTicker = String(queryTicker || hintedTicker || '')
                .trim()
                .toUpperCase();
            const availableTickers = new Set((items || []).map((item) => item.ticker));

            if (candidateTicker && availableTickers.has(candidateTicker)) {
                contextStore.setSelectedTicker(candidateTicker);
                return;
            }

            if (contextStore.selectedTicker && availableTickers.has(contextStore.selectedTicker)) {
                return;
            }

            contextStore.setSelectedTicker(items?.[0]?.ticker || '');
        },
        { immediate: true }
    );

    const selectedStock = computed(() => {
        const currentTicker = contextStore.selectedTicker;

        return results.value.find((item) => item.ticker === currentTicker) || results.value[0] || null;
    });

    const ohlcSeriesResource = useRemoteResource({
        key: () => chartingQueryKeys.ohlcSeries(sessionStore.uid, selectedStock.value?.ticker || '', contextStore.resolution),
        loader: () => fetchChartingOhlcSeries(selectedStock.value?.ticker || '', contextStore.resolution),
        ttl: 60000,
        enabled: () => sessionStore.isAuthenticated && Boolean(selectedStock.value?.ticker) && Boolean(unref(enableOhlcSeries))
    });

    const currentIndex = computed(() => results.value.findIndex((item) => item.ticker === selectedStock.value?.ticker));
    const hasPreviousTicker = computed(() => currentIndex.value > 0);
    const hasNextTicker = computed(() => currentIndex.value >= 0 && currentIndex.value < results.value.length - 1);

    const selectTicker = async (ticker) => {
        contextStore.setSelectedTicker(ticker);
        await syncSelectedTickerInRoute(ticker);
    };

    const selectEntryVariant = async (ticker, entryVariantKey) => {
        if (!ticker || !entryVariantKey) {
            return;
        }

        contextStore.setSelectedTicker(ticker);
        contextStore.setSelectedEntryVariant(ticker, entryVariantKey);
        await syncSelectedTickerInRoute(ticker);
    };

    const selectRelativeTicker = async (offset) => {
        const nextItem = results.value[currentIndex.value + offset];

        if (!nextItem?.ticker) {
            return;
        }

        await selectTicker(nextItem.ticker);
    };

    return {
        collectionResource,
        results,
        totalResults: computed(() => marketExplorer.isActive.value ? marketExplorer.total.value : results.value.length),
        hasMoreResults: computed(() => marketExplorer.isActive.value && marketExplorer.hasMore.value),
        isLoadingNextPage: computed(() => marketExplorer.isActive.value && Boolean(marketExplorer.state.value?.pageLoading)),
        loadNextPage: () => marketExplorer.isActive.value ? marketExplorer.loadNextPage() : Promise.resolve({ skipped: true }),
        resultSortOptions,
        resultSortValue,
        selectedStock,
        ohlcSeriesResource,
        hasPreviousTicker,
        hasNextTicker,
        selectTicker,
        selectEntryVariant,
        selectPreviousTicker: () => selectRelativeTicker(-1),
        selectNextTicker: () => selectRelativeTicker(1)
    };
}
